import { createServerFn } from "@tanstack/react-start";

export type ReviewWidgets = { trustoo: string; google: string };
type Stored = ReviewWidgets & { ownerUserId?: string; updatedAt?: string };

const BUCKET = "site-settings";
const FILE = "review-widgets.json";

async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function read(): Promise<Stored> {
  const sb = await admin();
  const { data, error } = await sb.storage.from(BUCKET).download(FILE);
  if (error || !data) return { trustoo: "", google: "" };
  try {
    const j = JSON.parse(await data.text());
    return { trustoo: j.trustoo ?? "", google: j.google ?? "", ownerUserId: j.ownerUserId, updatedAt: j.updatedAt };
  } catch {
    return { trustoo: "", google: "" };
  }
}

async function write(s: Stored) {
  const sb = await admin();
  const { error } = await sb.storage
    .from(BUCKET)
    .upload(FILE, new Blob([JSON.stringify(s)], { type: "application/json" }), { upsert: true });
  if (error) throw new Error(`Opslaan mislukt: ${error.message}`);
}

/** Openbaar: alleen de widgetcodes, zonder beheerdersgegevens. */
export const getReviewWidgets = createServerFn().handler(async (): Promise<ReviewWidgets> => {
  const s = await read();
  return { trustoo: s.trustoo, google: s.google };
});

export const getReviewAdmin = createServerFn().handler(async () => {
  const { getSessionUser } = await import("@/lib/auth.server");
  const user = await getSessionUser();
  if (!user) return { status: "signed_out" as const };
  const s = await read();
  if (s.ownerUserId && s.ownerUserId !== user.userId) return { status: "forbidden" as const };
  return {
    status: "ok" as const,
    claimed: !!s.ownerUserId,
    name: user.displayName ?? "",
    trustoo: s.trustoo,
    google: s.google,
    updatedAt: s.updatedAt ?? null,
  };
});

export const saveReviewWidgets = createServerFn({ method: "POST" })
  .inputValidator((d: ReviewWidgets) => {
    const clean = (v: unknown) => (typeof v === "string" ? v.slice(0, 20000) : "");
    return { trustoo: clean(d?.trustoo), google: clean(d?.google) };
  })
  .handler(async ({ data }) => {
    const { getSessionUser } = await import("@/lib/auth.server");
    const user = await getSessionUser();
    if (!user) throw new Error("Niet ingelogd");
    const s = await read();
    if (s.ownerUserId && s.ownerUserId !== user.userId) throw new Error("Geen toegang");
    await write({ ...data, ownerUserId: s.ownerUserId ?? user.userId, updatedAt: new Date().toISOString() });
    return { ok: true };
  });
